import React, { useState, useEffect } from "react";
import { adminMarketingOverview, socialStats, metaStatus, metaDisconnect, metaLoginUrl } from "../../api";
import { BarChart3, TrendingUp, Users, Target, PoundSterling, Megaphone, Facebook, Instagram, Send, Rocket, Eye, Info, CheckCircle2, Link2, Unlink, ShieldCheck } from "lucide-react";
import { useToast } from "../../hooks/use-toast";
import SocialPosts from "./social/SocialPosts";
import SocialAds from "./social/SocialAds";

const AnalyticsPanel = ({ data }) => {
  const { kpis, monthly, sources } = data;
  const maxVisits = Math.max(...monthly.map((m) => m.visits));
  const maxSource = Math.max(1, ...sources.map((s) => s.value));
  const kpiCards = [
    { icon: Users, label: "Website visits", value: kpis.websiteVisits.toLocaleString(), sub: `+${kpis.visitsChange}% vs last month` },
    { icon: Target, label: "Leads captured", value: kpis.leads, sub: `${kpis.conversionRate}% conversion` },
    { icon: PoundSterling, label: "Ad spend (mo)", value: `£${kpis.adSpend.toLocaleString()}`, sub: `${kpis.roas}x ROAS` },
    { icon: TrendingUp, label: "Conversion rate", value: `${kpis.conversionRate}%`, sub: "visits → leads" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpiCards.map((k) => {
          const Icon = k.icon;
          return (
            <div key={k.label} className="bg-white border border-black/10 p-5">
              <div className="flex items-center justify-between mb-3"><span className="text-xs text-gray-500 uppercase tracking-wide">{k.label}</span><Icon size={18} className="gpm-text-gold" /></div>
              <div className="text-2xl font-bold gpm-text-green">{k.value}</div>
              <div className="text-xs text-green-600 mt-1">{k.sub}</div>
            </div>
          );
        })}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-black/10 p-6">
          <h3 className="font-serif-display text-lg font-bold gpm-text-green mb-5">Visits & Leads — last 6 months</h3>
          <div className="flex items-end justify-between gap-3 h-48">
            {monthly.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full flex items-end justify-center gap-1 h-40">
                  <div className="w-1/2 rounded-t" style={{ height: `${(m.visits / maxVisits) * 100}%`, backgroundColor: "var(--gpm-green)" }} />
                  <div className="w-1/2 rounded-t" style={{ height: `${Math.min(100, (m.leads / 30) * 100)}%`, backgroundColor: "var(--gpm-gold)" }} />
                </div>
                <span className="text-xs text-gray-500">{m.month}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-black/10 p-6">
          <h3 className="font-serif-display text-lg font-bold gpm-text-green mb-5">Lead sources</h3>
          <div className="space-y-4">
            {sources.map((s) => (
              <div key={s.source}>
                <div className="flex justify-between text-sm mb-1"><span className="text-gray-600">{s.source}</span><span className="font-semibold gpm-text-green">{s.value}</span></div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(s.value / maxSource) * 100}%`, backgroundColor: "var(--gpm-green)" }} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketingHub = () => {
  const [tab, setTab] = useState("posts");
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [meta, setMeta] = useState(null);
  const { toast } = useToast();

  const loadStats = () => socialStats().then(setStats).catch(() => setStats(null));
  const loadMeta = () => metaStatus().then(setMeta).catch(() => setMeta(null));
  useEffect(() => {
    adminMarketingOverview().then(setAnalytics).catch(() => setAnalytics(null));
    loadMeta();
    loadStats();
    // Handle OAuth return
    const params = new URLSearchParams(window.location.search);
    const m = params.get("meta");
    if (m) {
      if (m === "connected") toast({ title: "Meta connected!", description: "Your Facebook & Instagram are now linked." });
      else if (m === "nopage") toast({ title: "No Facebook Page found", description: "Make sure your account manages a Page.", variant: "destructive" });
      else toast({ title: "Connection failed", description: "Please try connecting again.", variant: "destructive" });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []); // eslint-disable-line

  const connected = meta?.connected;
  const configured = meta?.configured;

  const disconnect = async () => {
    if (!window.confirm("Disconnect your Meta accounts? Posting will return to demo mode.")) return;
    await metaDisconnect();
    loadMeta();
    toast({ title: "Disconnected" });
  };

  const statCards = stats ? [
    { icon: Send, label: "Published posts", value: stats.published },
    { icon: Eye, label: "Total reach", value: (stats.totalReach || 0).toLocaleString() },
    { icon: Rocket, label: "Active ads", value: stats.activeAds },
    { icon: PoundSterling, label: "Ad spend", value: `£${(stats.totalSpend || 0).toLocaleString()}` },
  ] : [];

  const subTabs = [{ k: "posts", l: "Posts", icon: Send }, { k: "ads", l: "Ads", icon: Rocket }, { k: "analytics", l: "Analytics", icon: BarChart3 }];

  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <h2 className="font-serif-display text-2xl font-bold gpm-text-green">Marketing</h2>
        {connected
          ? <span className="text-[10px] font-semibold uppercase tracking-wide bg-green-100 text-green-800 px-2 py-1 rounded flex items-center gap-1"><ShieldCheck size={12} /> Live</span>
          : <span className="text-[10px] font-semibold uppercase tracking-wide bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Demo Mode</span>}
      </div>
      <p className="text-sm text-gray-500 mb-6">Create Facebook & Instagram posts, turn them into ads, and track performance — all from here.</p>

      {/* Meta connection */}
      <div className="mb-6 border border-black/10 bg-[#f4f2ea] p-5" data-testid="meta-connection">
        {connected ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={22} className="text-green-600" />
              <div>
                <p className="text-sm font-semibold gpm-text-green">Connected to Meta</p>
                <p className="text-xs text-gray-600 flex items-center gap-3 mt-0.5">
                  {meta.pageName && <span className="flex items-center gap-1"><Facebook size={12} style={{ color: "#1877F2" }} /> {meta.pageName}</span>}
                  {meta.instagramUsername && <span className="flex items-center gap-1"><Instagram size={12} style={{ color: "#DD2A7B" }} /> @{meta.instagramUsername}</span>}
                </p>
              </div>
            </div>
            <button data-testid="meta-disconnect" onClick={disconnect} className="flex items-center gap-2 px-4 py-2 border border-black/20 text-sm font-semibold text-red-600"><Unlink size={15} /> Disconnect</button>
          </div>
        ) : configured ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <Link2 size={20} className="gpm-text-green shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700 max-w-xl">Your Meta app is set up. Click connect, log in with Facebook and approve — your Page & Instagram will link and posts will publish for real.</p>
            </div>
            <a data-testid="meta-connect" href={metaLoginUrl()} className="flex items-center gap-2 px-5 py-2.5 gpm-text-cream text-sm font-semibold" style={{ backgroundColor: "#1877F2" }}><Facebook size={16} /> Connect Meta</a>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <Info size={18} className="gpm-text-green shrink-0 mt-0.5" />
            <p className="text-sm text-gray-700"><b>Preview mode.</b> Posts and ads are created and tracked here with sample results. To publish for real to Facebook & Instagram, your Meta App keys need to be added — send them to your developer and real posting switches on automatically.</p>
          </div>
        )}
      </div>

      {/* Stats header */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white border border-black/10 p-5">
              <div className="flex items-center justify-between mb-3"><span className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</span><Icon size={18} className="gpm-text-gold" /></div>
              <div className="text-2xl font-bold gpm-text-green">{s.value}</div>
            </div>
          );
        })}
      </div>

      {/* Sub tabs */}
      <div className="flex gap-2 mb-6 border-b border-black/10">
        {subTabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={`marketing-subtab-${t.k}`} onClick={() => setTab(t.k)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold uppercase tracking-wide border-b-2 transition-colors ${tab === t.k ? "gpm-text-green" : "text-gray-400 border-transparent hover:text-gray-600"}`} style={tab === t.k ? { borderColor: "var(--gpm-gold)" } : {}}>
              <Icon size={15} /> {t.l}
            </button>
          );
        })}
      </div>

      {tab === "posts" && <SocialPosts onChange={loadStats} />}
      {tab === "ads" && <SocialAds onChange={loadStats} />}
      {tab === "analytics" && (analytics ? <AnalyticsPanel data={analytics} /> : <div className="py-20 text-center text-gray-500">Loading analytics…</div>)}
    </div>
  );
};

export default MarketingHub;
